import createAsyncActions from 'shared/redux/helpers/create-async-actions';
import vodAdapter from 'shared/utils/adapters/vod';
import getBIUploadType from 'shared/utils/bi/get-bi-upload-type';
import { getUploadErrorType, UPLOAD_ERROR_TYPES } from 'shared/utils/video-helpers/statuses';
import uploadingStatuses from 'shared/constants/uploading-statuses';

import { normalizeVideo } from 'shared/redux/helpers/normalized';
import { subscribeOnProgressUpdate, subscribeOnAsyncUpdates } from 'dashboard/utils/progress-update-utils';

import { getUploadErrorType as getUploadErrorTypeForBi } from 'dashboard/analytics/bi/utils/utils';
import { toFakeVideoData, addDataToSameChannelVideos } from 'dashboard/redux/helpers/upload';
import { UPLOAD_EVENTS } from './upload-events';


const ACTIONS = createAsyncActions('SERVER.VIDEO.UPLOAD', ['START', 'ITEM_CREATED', 'ASYNC_UPLOAD_START', 'PROGRESS', 'SUCCESS', 'FAIL']);
export const NAMES = ACTIONS.NAMES;
const { START, ITEM_CREATED, ASYNC_UPLOAD_START, PROGRESS, SUCCESS, FAIL } = ACTIONS;

function createNewItem(response, state, fakeVideoData, dispatch, uploadKey, analytics) {
  response = {
    ...response,
    // mark item as newly created
    data: normalizeVideo({ ...response.data, isNew: true }),
  };
  addDataToSameChannelVideos(response.data, state);

  dispatch(SUCCESS(fakeVideoData, response, {
    analytics: {
      type: UPLOAD_EVENTS.SUCCESS,
      payload: { uploadKey, ...analytics },
    },
  }));
  return response;
}

function handleError(response, state, videoId, fakeVideoData, dispatch, uploadKey, analytics) {
  const { status, error } = response;
  const data = normalizeVideo({
    id: videoId,
    error,
    uploadingStatus: uploadingStatuses.FAILED,
  });
  addDataToSameChannelVideos(data, state);
  dispatch(FAIL(fakeVideoData, { ...response, data }, {
    analytics: _.filter([
      { type: UPLOAD_EVENTS.FAIL, payload: { response, uploadKey, ...analytics } },
      {
        type: 'detailed-error',
        payload: { name: 'upload-error', errorData: response },
      },
      getUploadErrorType(error) === UPLOAD_ERROR_TYPES.FAILED ?
      {
        type: 'event',
        payload: {
          name: 'bo.lib.upload.error',
          videoID: videoId,
          errorType: getUploadErrorTypeForBi(error),
        },
      } : null,
      status > 500 || !error ?
      {
        type: 'error',
        payload: { name: 'dashboard.video.upload' },
      } : null,
    ]),
  }));
  return Promise.reject({ ...response, data });
}

function upload(file, analytics) {
  return function (dispatch, getState) {
    const fakeVideoData = toFakeVideoData(file);

    const { vodSettings } = getState();
    const { defaultChannelId } = vodSettings;

    const uploadPromise = vodAdapter.item.upload({
      file,
      title: fakeVideoData.title,
      listId: defaultChannelId,
    });

    const uploadKey = Symbol('upload-key');
    const { on, off } = uploadPromise;
    _.assign(fakeVideoData, { on, off });

    dispatch(START(fakeVideoData, { data: normalizeVideo(fakeVideoData) },
      { analytics: { type: UPLOAD_EVENTS.START_ATTEMPT, payload: { uploadPromise, uploadKey, ...analytics } } }));

    let videoId = fakeVideoData.id;
    on(vodAdapter.events.ITEM.CREATE.SUCCESS, function (response) {
      videoId = response.data.id;
      response = {
        ...response,
        data: normalizeVideo({ ...fakeVideoData, ...response.data }),
      };
      dispatch(ITEM_CREATED(fakeVideoData, response));
    });


    subscribeOnProgressUpdate(uploadPromise, function ({ uploadingStatus }) {
      // TODO: move to video and channelVideo reducers, subscribed to all ACTIONS
      const data = normalizeVideo({
        id: videoId,
        uploadingStatus,
      });
      addDataToSameChannelVideos(data, getState());

      dispatch(PROGRESS(fakeVideoData, { data }));
    });


    return uploadPromise
      .then(
        response => createNewItem(response, getState(), fakeVideoData, dispatch, uploadKey, analytics),
        error => handleError(error, getState(), videoId, fakeVideoData, dispatch, uploadKey, analytics)
      );
  };
}

function uploadByUrl(file, analytics) {
  return function (dispatch, getState) {
    const fakeVideoData = toFakeVideoData(file);

    const { vodSettings } = getState();
    const { defaultChannelId } = vodSettings;

    const uploadPromise = vodAdapter.item.uploadByUrl({
      file,
      title: fakeVideoData.title,
      listId: defaultChannelId,
    });

    const uploadKey = Symbol('upload-key');
    const { on, off } = uploadPromise;
    _.assign(fakeVideoData, { on, off });

    dispatch(START(fakeVideoData, { data: normalizeVideo(fakeVideoData) },
      { analytics: { type: UPLOAD_EVENTS.START_ATTEMPT, payload: { uploadPromise, uploadKey, ...analytics } } }));

    let videoId = fakeVideoData.id;
    on(vodAdapter.events.ITEM.CREATE.SUCCESS, function (response) {
      videoId = response.data.id;
      response = {
        ...response,
        data: normalizeVideo({ ...fakeVideoData, ...response.data }),
        uploadingStatus: uploadingStatuses.UPLOADING,
      };
      dispatch(ITEM_CREATED(fakeVideoData, response));
    });


    let unsubscribeFn;

    return uploadPromise
      .then(({ videoFile, itemResponse }) => {
        const fileId = _.get(videoFile, 'data.id');

        // trigger bi event
        dispatch(ASYNC_UPLOAD_START(fakeVideoData, null, {
            analytics: {
              type: 'event',
              payload: {
                name: 'bo.upload.start',
                ...analytics,
                button: getBIUploadType(analytics.button),
                tracksUploaded: '1',
              },
            },
          })
        );

        return new Promise((resolve, reject) => {
          unsubscribeFn = subscribeOnAsyncUpdates(fileId, ({ uploadingStatus }) => {
            const data = normalizeVideo({
              id: videoId,
              uploadingStatus,
            });
            addDataToSameChannelVideos(data, getState());

            dispatch(PROGRESS(fakeVideoData, { data }));

            if (uploadingStatus === uploadingStatuses.SUCCEEDED) {
              // video file successfully uploaded
              unsubscribeFn();
              resolve(itemResponse);
            } else if (uploadingStatus === uploadingStatuses.FAILED) {
              // something went wrong
              unsubscribeFn();

              reject({
                data: {
                  result: fileId,
                  error: uploadingStatuses.FAILED,
                },
              });
            }
          });
        });
      })
      // upload updated item
      .then(itemResponse => vodAdapter.item.get(itemResponse.data.id))
      .then(
        response => createNewItem(response, getState(), fakeVideoData, dispatch, uploadKey, analytics),
        error => handleError(error, getState(), videoId, fakeVideoData, dispatch, uploadKey, analytics)
      );
  };
}

export {
  upload,
  uploadByUrl,
};



// WEBPACK FOOTER //
// ./src/dashboard/redux/server/actions/video/upload.js